import DecimalType from "decimal.js";
import { MEAL_PLAN_CALORIES, PouchSize } from "@./cat-calculations";
import { TFreshRecipeQuantities } from "@api-clients/subscriptions";
import { SerialType } from "@utils/types";

import {
  PricingPlan,
  SubscriptionItemType,
  SubscriptionItemVariantValue,
  SubscriptionType,
} from "@/primary-models";
import { TraySize } from "@/constants/products";

import { TFreshMealPlan, TLitterPlan } from "./subscriptions.types";

export interface ITraySizeDetails {
  price: DecimalType;
  calories: number;
}

export interface ITraySize extends ITraySizeDetails {
  traySize: PouchSize;
}

export type ITraySizeMap = Record<PouchSize, ITraySizeDetails>;

export interface IFreshPricingPlanDetails {
  quantity: number;
  cadence: number;
  traySize: ITraySizeMap;
  isTrialPlan: boolean;
}

export interface IFreshPricingPlan extends IFreshPricingPlanDetails {
  pricingPlan: PricingPlan;
}

export type TFreshMealPlanPricingMap = Record<PricingPlan, IFreshPricingPlanDetails>;

export function isFreshMealPlan(
  plan: SerialType<TFreshMealPlan | TLitterPlan>,
): plan is SerialType<TFreshMealPlan>;
export function isFreshMealPlan(
  plan: TFreshMealPlan | TLitterPlan,
): plan is TFreshMealPlan;
export function isFreshMealPlan(
  plan: SerialType<TFreshMealPlan | TLitterPlan> | TFreshMealPlan | TLitterPlan,
): plan is SerialType<TFreshMealPlan> | TFreshMealPlan {
  return plan.type === SubscriptionType.FRESH;
}
export function isPlanetPlan(plan: TFreshMealPlan | TLitterPlan): plan is TLitterPlan {
  return plan.type === SubscriptionType.SCOOP_PLANET;
}
export function isHealthScoopPlan(plan: TFreshMealPlan | TLitterPlan): plan is TLitterPlan {
  return !isFreshMealPlan(plan) && !isPlanetPlan(plan);
}

export const getCaloriesFromPouchSize = (pouchSize: PouchSize) => MEAL_PLAN_CALORIES[pouchSize];
export interface IUpdateMealPlanOrTraySizeRequest {
  subscriptions: IUpdateMealPlanOrTraySizeParams[];
}
export interface IUpdateMealPlanOrTraySizeParams {
  subscriptionId: number;
  mealPlan?: PricingPlan | null;
  traySize?: TraySize | null;
}

export interface IUpdateMealPlanOrTraySizeResponse {
  subscriptions: IUpdateMealPlanOrTraySizeResponseData[];
}

export interface IUpdateMealPlanOrTraySizeResponseData {
  subscriptionId: number;
  mealPlan: PricingPlan;
  traySize: TraySize;
}

export interface IUpdatedMealPlanLogData {
  mealPlan: PricingPlan;
  recipes: Partial<TFreshRecipeQuantities>;
}

export interface IUpdatedTraySizeLogData {
  traySize: TraySize;
}

export interface IUpdatedSubscriptionFrequencyLogData {
  frequencyInDays: number;
}

export interface IUpdatedScoopQuantityLogData {
  subscriptionItemType: SubscriptionItemType;
  quantity: number;
}

const POUCH_SIZE_VALUES: ReadonlySet<PouchSize> = new Set([ "A", "B", "C", "D", "E", "F" ]);

export const isPouchSize = (value: SubscriptionItemVariantValue): value is PouchSize => (
  POUCH_SIZE_VALUES.has(value as PouchSize)
);
